// The instructor's toggle: which provider answers the S-2 estimate.
//
// A chip group, not a dropdown (spec §8, and Michael's rule). Two chips and a status line that answers
// the three questions somebody actually has when they flip it: is there a key behind the proxy, has the
// calibration been fitted and when, and what did the last call cost in milliseconds.
//
// That status line is the whole reason this is not a bare switch. On this app's fallback design a Jev
// call with no key behind it is *completely silent* — the heuristic answers, the card looks identical,
// and nobody finds out until they claim in a demo that they are looking at a model's answer. So the
// line says `no key` when there is no key, and it says `last call 118 ms` when there was one.
//
// Insertion into `InstructorPanel.tsx` — one line, inside the panel body, under the sliders grid:
//
//     <JevToggle />
//
// Also reachable without the panel: `?jev=1` on any URL forces it on for that page load, `?jev=0` off.
// That is handled inside `src/ai/jevFlag.ts`, not in `App.tsx`, so no routing code has to know.

import { useSyncExternalStore } from 'react';
import { Chip } from './cards/ui';
import {
  getJevFlag, jevFlagSnapshot, jevUrlOverride, setJevEnabled, subscribeJevFlag,
} from '../ai/jevFlag';
import { jevHealth, subscribeJevHealth } from '../ai/jevClient';
import { calibrationStatus } from '../ai/calibration';

export function JevToggle() {
  // The flag and the proxy health are both module-level external stores; `useSyncExternalStore` is the
  // right subscription for each, and keeps this component honest about re-rendering on change.
  useSyncExternalStore(subscribeJevFlag, jevFlagSnapshot, jevFlagSnapshot);
  const health = useSyncExternalStore(subscribeJevHealth, jevHealth, jevHealth);

  const flag = getJevFlag();
  const override = jevUrlOverride();
  const calibration = calibrationStatus();

  const statusBits: string[] = [];

  if (health.calls === 0) {
    statusBits.push('no call made yet this tab');
  } else if (health.lastOk) {
    statusBits.push(`last call ${Math.round(health.lastLatencyMs ?? 0)} ms`);
  } else if (/missing JEV_API_KEY/.test(health.lastError ?? '')) {
    statusBits.push('no key behind the proxy - the heuristic is answering');
  } else {
    statusBits.push(`last call failed: ${(health.lastError ?? '').slice(0, 80)}`);
  }

  statusBits.push(
    calibration.present
      ? `calibrated on ${calibration.runs.toLocaleString()} fights`
        + `${calibration.generatedAt ? `, fitted ${new Date(calibration.generatedAt).toISOString().slice(0, 10)}` : ''}`
      : 'no calibration fitted - probabilities are raw',
  );

  if (health.failures > 0) statusBits.push(`${health.failures} of ${health.calls} calls fell back`);

  return (
    <div className="wt-card" data-testid="jev-toggle">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="mono text-[10px] uppercase tracking-[0.12em] text-paper">S-2 estimates</span>
        <Chip
          active={!flag.enabled}
          onClick={() => setJevEnabled(false)}
          testId="jev-provider-heuristic"
          title="Deterministic functions over the common operating picture. Instant, offline, reproducible."
        >
          heuristic
        </Chip>
        <Chip
          active={flag.enabled}
          onClick={() => setJevEnabled(true)}
          testId="jev-provider-jev"
          title="Jev (TypeSafe AI's System One model): typed judgments with probabilities, behind /api/jev. Falls back to the heuristic on any failure."
        >
          Jev
        </Chip>
        {override !== undefined && (
          <span className="mono text-[9px] uppercase tracking-[0.1em]" style={{ color: 'var(--color-warn)' }}>
            forced by ?jev={override ? '1' : '0'}
          </span>
        )}
      </div>

      <p className="mono mt-1.5 text-[10px] leading-snug text-fog" data-testid="jev-toggle-status">
        {statusBits.join(' · ')}
      </p>

      <p className="mt-1 text-[10px] leading-snug text-fog">
        Three questions - which third holds the enemy main effort, what a given contact is, and whether the
        reserve commits in the next ten minutes - answered as a type with a probability, never as prose.
        Either provider answers the same interface; a Jev call that is slow, unkeyed or refused is served by
        the heuristic and the AI audit records which one answered.
      </p>
    </div>
  );
}
